import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useKnowledgeBaseDocument, deleteDocument } from '../hooks/useKnowledgeBase';
import Button from '@/common/ui/components/Button';

// Simple markdown to HTML converter
function renderMarkdown(markdown: string): string {
  let html = markdown;

  // Headers
  html = html.replace(/^### (.*$)/gim, '<h3 class="text-xl font-semibold text-gray-900 mt-6 mb-3">$1</h3>');
  html = html.replace(/^## (.*$)/gim, '<h2 class="text-2xl font-bold text-gray-900 mt-8 mb-4">$1</h2>');
  html = html.replace(/^# (.*$)/gim, '<h1 class="text-3xl font-bold text-gray-900 mt-10 mb-6">$1</h1>');

  // Bold
  html = html.replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold text-gray-900">$1</strong>');

  // Code blocks
  html = html.replace(/`([^`]+)`/g, '<code class="bg-gray-100 px-2 py-1 rounded text-sm font-mono text-gray-800">$1</code>');

  // Lists
  html = html.replace(/^\* (.*$)/gim, '<li class="ml-6 list-disc">$1</li>');
  html = html.replace(/^- (.*$)/gim, '<li class="ml-6 list-disc">$1</li>');
  html = html.replace(/^\d+\. (.*$)/gim, '<li class="ml-6 list-decimal">$1</li>');

  // Paragraphs
  html = html.split('\n\n').map(para => {
    if (para.match(/^<[h1-6|li]/)) return para;
    return `<p class="text-gray-700 leading-relaxed mb-4">${para}</p>`;
  }).join('\n');

  // Line breaks
  html = html.replace(/\n/g, '<br>');

  return html;
}

export default function KnowledgeBaseViewer() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { document, loading, error } = useKnowledgeBaseDocument(id);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!document) return;

    if (!window.confirm(`Are you sure you want to delete "${document.title}"? This action cannot be undone.`)) {
      return;
    }

    setDeleting(true);
    const result = await deleteDocument(document.id);

    if (result.success) {
      navigate('/admin/knowledge-base');
    } else {
      alert(`Failed to delete document: ${result.error}`);
      setDeleting(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      'Operations': 'bg-blue-100 text-blue-800',
      'Technical': 'bg-purple-100 text-purple-800',
      'Legal': 'bg-red-100 text-red-800',
      'Safety': 'bg-orange-100 text-orange-800',
      'Process': 'bg-green-100 text-green-800',
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-600">Loading document...</div>
      </div>
    );
  }

  if (error || !document) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || 'Document not found'}</p>
          <Button onClick={() => navigate('/admin/knowledge-base')} variant="primary">
            Back to Knowledge Base
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow print:shadow-none">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6 print:py-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <span className={`px-3 py-1 text-xs font-medium rounded-full ${getCategoryColor(document.category)}`}>
                  {document.category}
                </span>
              </div>
              <h1 className="text-title-lg font-inter text-text-primary">
                {document.title}
              </h1>
              <p className="text-caption text-text-secondary font-inter mt-1">
                Last updated: {formatDate(document.updatedAt)}
              </p>
            </div>
            <div className="flex gap-3 print:hidden">
              <Button
                onClick={() => navigate('/admin/knowledge-base')}
                variant="ghost"
                size="sm"
              >
                ← Back
              </Button>
              <Button
                onClick={handlePrint}
                variant="ghost"
                size="sm"
              >
                🖨️ Print
              </Button>
              <Button
                onClick={() => navigate(`/admin/knowledge-base/${document.id}/edit`)}
                variant="ghost"
                size="sm"
              >
                Edit
              </Button>
              <Button
                onClick={handleDelete}
                variant="ghost"
                size="sm"
                loading={deleting}
                disabled={deleting}
                className="text-red-600 hover:bg-red-50"
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 print:py-4">
        <div className="bg-white rounded-lg shadow print:shadow-none p-8 print:p-4">
          <div
            className="prose prose-sm max-w-none"
            dangerouslySetInnerHTML={{ __html: renderMarkdown(document.content) }}
          />
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body {
            background: white;
          }
          .print\\:hidden {
            display: none !important;
          }
          .print\\:shadow-none {
            box-shadow: none !important;
          }
          .print\\:py-4 {
            padding-top: 1rem !important;
            padding-bottom: 1rem !important;
          }
          .print\\:p-4 {
            padding: 1rem !important;
          }
        }
      `}</style>
    </div>
  );
}
